/** F step 7c — score-rule classification helpers (score-degradation, reports, sparklines). */

export type ScoreKind = 'a' | 'ac' | 'at' | 'act' | 'p' | 'pc' | 'pt' | 'pct';

/**
 * Classifies the scoring-rule mix of an audit bucket.
 * - absent/empty → `'rubric'` (legacy, no new-rule rows)
 * - only `image_v1` (no `rubric_v0`, no `compare_v1`) → `'image'`
 * - only `compare_v1` / `compare_v2` (no `image_v1`) → `'compare'`
 * - `image_v1` + `rubric_v0` or `compare_v1` → `` `mixed:${pct}` ``
 *   where `pct = Math.round(image_v1 / total * 100)`.
 */
export function ruleMixKey(
  scoreRuleCounts?: Record<string, number>,
): 'rubric' | 'image' | 'compare' | `mixed:${number}` {
  if (!scoreRuleCounts) return 'rubric';
  const imageV1 = scoreRuleCounts['image_v1'] ?? 0;
  // Both comparison rules count as 'compare' (compare_v2 replaced compare_v1 on 2026-11-02).
  const compareV1 = (scoreRuleCounts['compare_v1'] ?? 0) + (scoreRuleCounts['compare_v2'] ?? 0);
  const rubricV0 = scoreRuleCounts['rubric_v0'] ?? 0;
  if (imageV1 === 0 && compareV1 === 0) return 'rubric';
  if (imageV1 > 0 && compareV1 === 0 && rubricV0 === 0) return 'image';
  if (compareV1 > 0 && imageV1 === 0) return 'compare';
  const total = imageV1 + rubricV0 + compareV1;
  return `mixed:${Math.round((imageV1 / total) * 100)}`;
}

/** Key for grouping a per-prompt series point by its scoring kind. */
export function scoreSeriesKey(point: { k?: string; scoreKind?: string }): string {
  return point.k ?? point.scoreKind ?? '';
}
